import React, { useState, useEffect, useCallback} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'react-hot-toast';
import { Loader2, ArrowLeft, Save, AlertTriangle, CheckCircle2, XCircle, DollarSign, Lock, Unlock } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerCreditView = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [creditData, setCreditData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    credit_limit: 0,
    credit_blocked: false,
    block_reason: '',
    risk_category: 'LOW'
  });


  const loadCreditData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/otc/credit/${customerId}`);
      setCreditData(response.data);
      setFormData({
        credit_limit: response.data.credit_limit || 0,
        credit_blocked: response.data.credit_blocked || false,
        block_reason: response.data.block_reason || '',
        risk_category: response.data.risk_category || 'LOW'
      });
    } catch (error) {
      console.error('Error loading credit data:', error);
      toast.error('Failed to load customer credit data');
    } finally {
      setIsLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    loadCreditData();
  }, [loadCreditData]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await axios.put(`${BACKEND_URL}/api/otc/credit/${customerId}`, formData);
      setCreditData(response.data);
      toast.success('Credit settings updated successfully');
      setEditMode(false);
    } catch (error) {
      console.error('Error saving credit data:', error);
      toast.error(error.response?.data?.detail || 'Failed to save credit settings');
    } finally {
      setIsSaving(false);
    }
  };

  const getStatusBadge = (data) => {
    if (data.credit_blocked) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-full bg-red-100 text-red-800">
          <XCircle className="w-4 h-4" /> Manually Blocked
        </span>
      );
    }
    if (data.available_credit < 0) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-full bg-red-100 text-red-800">
          <AlertTriangle className="w-4 h-4" /> Over Limit
        </span>
      );
    }
    if (data.available_credit < data.credit_limit * 0.2) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-full bg-yellow-100 text-yellow-800">
          <AlertTriangle className="w-4 h-4" /> Warning
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-full bg-green-100 text-green-800">
        <CheckCircle2 className="w-4 h-4" /> OK
      </span>
    );
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          <span className="ml-3">Loading credit data...</span>
        </div>
      </Layout>
    );
  }

  if (!creditData) {
    return (
      <Layout>
        <div className="p-6 text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-4">Customer Not Found</h1>
          <Button onClick={() => navigate('/otc/credit-exposure')}>Back to Credit Exposure</Button>
        </div>
      </Layout>
    );
  }

  const utilizationPercent = creditData.credit_limit > 0 
    ? (creditData.total_exposure / creditData.credit_limit) * 100 
    : 0;

  return (
    <Layout>
      <div className="p-6" data-testid="customer-credit-view">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/otc/credit-exposure')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Customer Credit Management</h1>
              <p className="text-sm text-gray-600">{customerId}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {getStatusBadge(creditData)}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Credit Overview */}
          <Card className="p-6 lg:col-span-2">
            <h3 className="text-lg font-semibold mb-4">Credit Overview</h3>
            
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-orange-50 rounded-lg p-4">
                <p className="text-xs text-orange-600 font-medium">Credit Limit</p>
                <p className="text-xl font-bold text-orange-900">
                  ${creditData.credit_limit.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div className="bg-orange-50 rounded-lg p-4">
                <p className="text-xs text-orange-600 font-medium">Total Exposure</p>
                <p className="text-xl font-bold text-orange-900">
                  ${creditData.total_exposure.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div className={`rounded-lg p-4 ${creditData.available_credit < 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                <p className={`text-xs font-medium ${creditData.available_credit < 0 ? 'text-red-600' : 'text-green-600'}`}>Available Credit</p>
                <p className={`text-xl font-bold ${creditData.available_credit < 0 ? 'text-red-900' : 'text-green-900'}`}>
                  ${creditData.available_credit.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div className="bg-orange-50 rounded-lg p-4">
                <p className="text-xs text-orange-600 font-medium">Utilization</p>
                <p className="text-xl font-bold text-orange-900">{utilizationPercent.toFixed(1)}%</p>
              </div>
            </div>

            {/* Utilization Bar */}
            <div className="mb-6">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Credit Utilization</span>
                <span className="font-medium">{utilizationPercent.toFixed(1)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div
                  className={`h-4 rounded-full transition-all ${
                    utilizationPercent >= 100 ? 'bg-red-500' :
                    utilizationPercent >= 80 ? 'bg-yellow-500' :
                    utilizationPercent >= 60 ? 'bg-orange-500' : 'bg-green-500'
                  }`}
                  style={{ width: `${Math.min(utilizationPercent, 100)}%` }}
                />
              </div>
            </div>

            {/* Exposure Breakdown */}
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Exposure Breakdown</h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-700">Open AR (Unpaid Invoices)</span>
                <span className="font-mono font-medium">
                  ${creditData.total_receivables.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-700">Open Orders Value</span>
                <span className="font-mono font-medium">
                  ${creditData.open_orders_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-700">Open Deliveries (Unbilled)</span>
                <span className="font-mono font-medium">
                  ${creditData.open_deliveries_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-orange-100 rounded-lg font-semibold">
                <span className="text-orange-900">Total Exposure</span>
                <span className="font-mono text-orange-900">
                  ${creditData.total_exposure.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </Card>

          {/* Credit Settings */}
          <Card className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Credit Settings</h3>
              {!editMode ? (
                <Button variant="outline" size="sm" onClick={() => setEditMode(true)}>Edit</Button>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => setEditMode(false)}>Cancel</Button>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <Label>Credit Limit ({creditData.credit_limit_currency})</Label>
                {editMode ? (
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.credit_limit}
                    onChange={(e) => setFormData({...formData, credit_limit: parseFloat(e.target.value) || 0})}
                  />
                ) : (
                  <p className="text-lg font-mono font-medium mt-1">
                    ${creditData.credit_limit.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </p>
                )}
              </div>

              <div>
                <Label>Risk Category</Label>
                {editMode ? (
                  <Select value={formData.risk_category} onValueChange={(v) => setFormData({...formData, risk_category: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LOW">Low Risk</SelectItem>
                      <SelectItem value="MEDIUM">Medium Risk</SelectItem>
                      <SelectItem value="HIGH">High Risk</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="mt-1">
                    <span className={`px-2 py-1 rounded text-sm font-medium ${
                      creditData.risk_category === 'HIGH' ? 'bg-red-100 text-red-800' :
                      creditData.risk_category === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {creditData.risk_category}
                    </span>
                  </p>
                )}
              </div>

              <div>
                <Label>Payment Behavior</Label>
                <p className="mt-1">
                  <span className="px-2 py-1 bg-gray-100 rounded text-sm">
                    {creditData.payment_behavior}
                  </span>
                </p>
              </div>

              <div className="border-t pt-4">
                <Label className="flex items-center gap-2">
                  {creditData.credit_blocked ? <Lock className="w-4 h-4 text-red-600" /> : <Unlock className="w-4 h-4 text-green-600" />}
                  Credit Block Status
                </Label>
                {editMode ? (
                  <div className="mt-2">
                    <Select 
                      value={formData.credit_blocked ? 'BLOCKED' : 'ACTIVE'} 
                      onValueChange={(v) => setFormData({...formData, credit_blocked: v === 'BLOCKED'})}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ACTIVE">Active (Unblocked)</SelectItem>
                        <SelectItem value="BLOCKED">Blocked</SelectItem>
                      </SelectContent>
                    </Select>
                    {formData.credit_blocked && (
                      <div className="mt-2">
                        <Input
                          placeholder="Block reason"
                          value={formData.block_reason}
                          onChange={(e) => setFormData({...formData, block_reason: e.target.value})}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="mt-1">
                    {creditData.credit_blocked ? (
                      <span className="text-red-600 font-medium">
                        Blocked {creditData.block_reason && `- ${creditData.block_reason}`}
                      </span>
                    ) : (
                      <span className="text-green-600 font-medium">Active</span>
                    )}
                  </p>
                )}
              </div>

              {editMode && (
                <div className="pt-4">
                  <Button className="w-full" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                    ) : (
                      <><Save className="w-4 h-4 mr-2" /> Save Changes</>
                    )}
                  </Button>
                </div>
              )}
            </div>

            {/* Last Check Info */}
            {creditData.last_credit_check && (
              <div className="mt-6 pt-4 border-t text-sm text-gray-500">
                <p>Last credit check: {new Date(creditData.last_credit_check).toLocaleString()}</p>
              </div>
            )}
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default CustomerCreditView;
